import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { AlertTriangle, TrendingUp, Calendar, Shield, Zap, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function ExploitIntelligence() {
  const { isDarkMode } = useTheme();
  const [epssData, setEpssData] = useState(null);
  const [kevData, setKevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [epssRes, kevRes] = await Promise.all([
        api.get('/exploit-intelligence/epss'),
        api.get('/exploit-intelligence/kev')
      ]);
      setEpssData(epssRes.data);
      setKevData(kevRes.data);
    } catch (error) {
      toast.error('Failed to fetch exploit intelligence data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const getEpssRiskColor = (score) => {
    if (score >= 0.7) return 'text-red-400 bg-red-500/20';
    if (score >= 0.4) return 'text-orange-400 bg-orange-500/20';
    if (score >= 0.2) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-96 ${isDarkMode ? '' : ''}`}>
        <div className={`font-rajdhani text-xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Loading exploit intelligence...</div>
      </div>
    );
  }

  return (
    <div data-testid="exploit-intel-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-4xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            Exploit Intelligence
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>EPSS scores and CISA Known Exploited Vulnerabilities</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-indigo-600 hover:bg-indigo-500 text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`rounded-xl p-5 border-l-4 border-red-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>High Risk EPSS</span>
          </div>
          <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{epssData?.high_risk || 0}</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Score &gt; 0.5</p>
        </div>
        
        <div className={`rounded-xl p-5 border-l-4 border-orange-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Shield className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>In CISA KEV</span>
          </div>
          <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{kevData?.total_in_kev || 0}</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Known Exploited</p>
        </div>
        
        <div className={`rounded-xl p-5 border-l-4 border-yellow-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Overdue KEV</span>
          </div>
          <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{kevData?.overdue || 0}</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Past Due Date</p>
        </div>
        
        <div className={`rounded-xl p-5 border-l-4 border-indigo-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Zap className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Total Analyzed</span>
          </div>
          <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{epssData?.total || 0}</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>CVEs with EPSS</p>
        </div>
      </div>

      <Tabs defaultValue="epss" className="w-full">
        <TabsList className={isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}>
          <TabsTrigger value="epss" className={isDarkMode ? 'data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400' : 'data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700'}>
            <TrendingUp className="w-4 h-4 mr-2" />
            EPSS Scores
          </TabsTrigger>
          <TabsTrigger value="kev" className={isDarkMode ? 'data-[state=active]:bg-red-600/20 data-[state=active]:text-red-400' : 'data-[state=active]:bg-red-100 data-[state=active]:text-red-700'}>
            <AlertTriangle className="w-4 h-4 mr-2" />
            CISA KEV Catalog
          </TabsTrigger>
        </TabsList>

        <TabsContent value="epss" className="mt-5">
          <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Exploit Prediction Scoring System (EPSS)</h3>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Probability of exploitation in the next 30 days</p>
            </div>
            
            <div className="scroll-container">
              <table className="w-full min-w-[600px]">
                <thead className={isDarkMode ? 'bg-zinc-900/70' : 'bg-slate-50'}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>CVE ID</th>
                    <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Title</th>
                    <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Severity</th>
                    <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>EPSS Score</th>
                    <th className={`px-4 py-3 text-left text-xs font-rajdhani font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Percentile</th>
                  </tr>
                </thead>
                <tbody className={isDarkMode ? 'divide-y divide-white/5' : 'divide-y divide-slate-200'}>
                  {epssData?.vulnerabilities?.slice(0, 20).map((vuln) => (
                    <tr key={vuln.vulnerability_id} className={isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}>
                      <td className="px-4 py-3">
                        <a 
                          href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-400 hover:text-indigo-300 font-mono text-sm flex items-center gap-1"
                        >
                          {vuln.cve_id}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-zinc-300 text-sm truncate max-w-xs block">{vuln.title}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs uppercase ${
                          vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                          vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {vuln.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-sm font-mono ${getEpssRiskColor(vuln.epss_score)}`}>
                            {(vuln.epss_score * 100).toFixed(2)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-zinc-400 text-sm">{vuln.epss_percentile?.toFixed(1)}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="kev" className="mt-5">
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10 bg-red-500/5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="font-rajdhani font-semibold text-lg text-red-400">CISA Known Exploited Vulnerabilities</h3>
              </div>
              <p className="text-zinc-500 text-xs mt-1">Vulnerabilities known to be actively exploited in the wild</p>
            </div>
            
            <div className="scroll-container">
              <table className="w-full min-w-[600px]">
                <thead className="bg-zinc-900/70">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase">CVE ID</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Vulnerability</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Date Added</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {kevData?.kev_vulnerabilities?.map((kev) => {
                    const isOverdue = new Date(kev.due_date) < new Date();
                    return (
                      <tr key={kev.vulnerability_id} className={`hover:bg-white/5 ${isOverdue ? 'bg-red-500/5' : ''}`}>
                        <td className="px-4 py-3">
                          <a 
                            href={`https://nvd.nist.gov/vuln/detail/${kev.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-red-400 hover:text-red-300 font-mono text-sm flex items-center gap-1"
                          >
                            {kev.cve_id}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-zinc-300 text-sm">{kev.title}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-zinc-400 text-sm">{kev.date_added}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${isOverdue ? 'text-red-400 font-bold' : 'text-zinc-400'}`}>
                            {kev.due_date}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            isOverdue ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {isOverdue ? 'OVERDUE' : 'ACTION REQUIRED'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {kevData?.kev_vulnerabilities?.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-12 text-center">
                        <Shield className="w-12 h-12 text-green-500 mx-auto mb-3" />
                        <p className="text-green-400">No vulnerabilities in KEV catalog</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
